import { FC, useState } from 'react';
import {
  ExternalLink,
  AlertCircle,
  Ban,
  CalendarDays,
  Video,
  Mail,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { SxmBooking } from '../types';

// --- Utility Components ---

const StatusBadge = ({ status }: { status: string }) => {
  const styles = {
    confirmed: 'text-gray-700 bg-white ring-1 ring-inset ring-gray-200',
    completed: 'text-gray-700 bg-white ring-1 ring-inset ring-gray-200',
    cancelled: 'text-gray-700 bg-white ring-1 ring-inset ring-gray-200',
    rescheduled: 'text-gray-700 bg-white ring-1 ring-inset ring-gray-200',
  };

  const dotColors = {
    confirmed: 'bg-emerald-500',
    completed: 'bg-blue-500',
    cancelled: 'bg-red-500',
    rescheduled: 'bg-amber-500',
  };

  const labels: Record<string, string> = {
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rescheduled: 'Rescheduled',
  };

  const styleClass = styles[status as keyof typeof styles] || 'text-gray-600 bg-gray-50 ring-1 ring-inset ring-gray-500/10';
  const dotClass = dotColors[status as keyof typeof dotColors] || 'bg-gray-400';
  const label = labels[status as keyof typeof labels] || status;

  return (
    <span className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium shadow-sm ${styleClass}`}>
      <div className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
};

const PlatformIcon = ({ provider }: { provider?: string }) => {
  if (!provider) return <CalendarDays className="w-4 h-4 text-gray-400" />;

  if (provider.toLowerCase().includes('google')) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-600 font-medium bg-white border border-gray-200 px-2 py-1 rounded shadow-sm">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4" />
        </svg>
        Google Meet
      </div>
    );
  }

  if (provider.toLowerCase().includes('outlook') || provider.toLowerCase().includes('office')) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-600 font-medium bg-white border border-gray-200 px-2 py-1 rounded shadow-sm">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 17L1 7L6 3.5L6 20.5L1 17ZM12.25 15.5L7 17.5V6.5L12.25 8.5V15.5ZM23 18.5L13.25 21V3L23 5.5V18.5Z" fill="#0072C6" />
        </svg>
        Outlook
      </div>
    );
  }

  return <Video className="w-4 h-4 text-gray-400" />;
};


// --- Main Component ---

interface SxmAppointmentsTabProps {
  bookings: SxmBooking[];
  totalBookings: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  error?: string | null;
  userId?: string;
  cancelBooking: (id: string) => Promise<void>;
}

export const SxmAppointmentsTab: FC<SxmAppointmentsTabProps> = ({
  bookings,
  totalBookings,
  currentPage,
  itemsPerPage,
  onPageChange,
  loading,
  error,
  cancelBooking
}) => {
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleCancel = async (booking: SxmBooking) => {
    if (!confirm('Are you sure you want to cancel this meeting? This action cannot be undone.')) return;
    setProcessingId(booking.id);
    try {
      await cancelBooking(booking.id);
    } finally {
      setProcessingId(null);
    }
  };

  const totalPages = Math.ceil(totalBookings / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  if (loading && bookings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
        <p className="text-gray-500 text-sm">Loading your schedule...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-700">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Your Schedule</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your upcoming sales meetings</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-[180px]">Date & Time</th>
                <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Lead</th>
                <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-[120px]">Status</th>
                <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider text-right w-[200px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <CalendarDays className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900">No meetings scheduled</h3>
                      <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
                        Your calendar is clear. Meetings booked through your lead forms will appear here automatically.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                bookings.map((booking: SxmBooking) => (
                  <tr key={booking.id} className="hover:bg-gray-50/80 transition-colors group">
                    {/* Date Column */}
                    <td className="py-4 px-6 whitespace-nowrap align-top">
                      <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center justify-center bg-gray-50 border border-gray-200 rounded-lg w-14 h-14 shadow-sm">
                          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                            {new Date(booking.date).toLocaleString('default', { month: 'short' })}
                          </span>
                          <span className="text-xl font-bold text-gray-900 leading-none pb-1">
                            {new Date(booking.date).getDate()}
                          </span>
                        </div>
                        <div className="flex flex-col pt-0.5">
                          <span className="text-sm font-semibold text-gray-900">
                            {new Date(`2000-01-01T${booking.time}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <div className="mt-1">
                            <PlatformIcon provider="google" />
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Lead Column */}
                    <td className="py-4 px-6 align-middle">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 shrink-0 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 font-bold text-sm shadow-sm">
                          {booking.lead?.email?.[0]?.toUpperCase() || <Mail className="w-4 h-4" />}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-gray-900 truncate max-w-[250px]" title={booking.lead?.email}>
                            {booking.lead?.email || 'Unknown Lead'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Status Column */}
                    <td className="py-4 px-6 whitespace-nowrap align-middle">
                      <StatusBadge status={booking.status} />
                    </td>


                    {/* Actions Column */}
                    <td className="py-4 px-6 whitespace-nowrap text-right align-middle">
                      <div className="flex items-center justify-end gap-3">
                        <div className="flex items-center justify-end gap-2">
                          {/* Calendar Link */}
                          {booking.htmlLink && booking.status !== 'cancelled' && (
                            <a
                              href={booking.htmlLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-transparent hover:border-indigo-100"
                              title="View in Calendar"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}

                          {/* Join Button */}
                          {(booking.meetingLink || booking.htmlLink) && booking.status !== 'cancelled' ? (
                            <a
                              href={booking.meetingLink || booking.htmlLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:text-emerald-800 transition-all shadow-sm"
                            >
                              <Video className="w-3.5 h-3.5" />
                              Join
                            </a>
                          ) : (booking.status !== 'cancelled' &&
                            <span className="text-xs text-gray-400 italic px-2">No link</span>
                          )}
                        </div>

                        {booking.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancel(booking)}
                            disabled={processingId === booking.id}
                            className="inline-flex items-center justify-center p-2 text-gray-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
                            title="Cancel Meeting"
                          >
                            {processingId === booking.id ? (
                              <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                            ) : (
                              <Ban className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalBookings > itemsPerPage && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(startIndex + itemsPerPage, totalBookings)}</span> of{' '}
                  <span className="font-medium">{totalBookings}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div >
    </div >
  );
};